<script setup lang="ts">
  import { TableCell, TableRow } from '@/components/ui/table'
  import { computed, defineProps, type PropType, ref } from 'vue'
  import { type CustomerDataMapper as DataMapper } from '@/types/generated/types.gen'
  import {
    Building2Icon,
    User,
    ArrowDownNarrowWideIcon,
    ArrowDownIcon,
    ArrowRightIcon,
    ChevronDownIcon,
    ChevronRightIcon
  } from 'lucide-vue-next'
  import TEMPORARYMapperDataTableCell from '@/components/app/cases/TEMPORARYMapperDataTableCell.vue'
  import TEMPORARYMapperSourceTableCell from '@/components/app/cases/TEMPORARYMapperSourceTableCell.vue'

  const props = defineProps({
    entityUuid: {
      type: String,
      required: true
    },
    mappers: {
      type: Array as PropType<DataMapper[]>,
      required: true
    },
    showMappersWithValues: {
      type: Boolean,
      default: false
    },
    search: {
      type: String,
      default: ''
    }
  })
  const entityMappers = computed<DataMapper[]>(() => {
    // Priority keys as substrings
    const priorityKeys = [
      'is_company',
      'company_name',
      'company_legal_form',
      'title',
      'specific_title',
      'first_name',
      'last_name',
      'street',
      'street_number',
      'zip',
      'city'
    ]

    return props.mappers
      .filter((mapper) => {
        // Filter by entity_uuid
        return mapper.entity_uuid === props.entityUuid
      })
      .sort((a, b) => {
        // Find the first matching priority key in "a.key" and "b.key"
        const rankA = priorityKeys.findIndex((priority) => a.key.includes(priority))
        const rankB = priorityKeys.findIndex((priority) => b.key.includes(priority))

        // If a key matches, use its index for sorting. If not, treat it as low priority.
        const finalRankA = rankA === -1 ? priorityKeys.length : rankA
        const finalRankB = rankB === -1 ? priorityKeys.length : rankB

        // Sort by the computed rank
        return finalRankA - finalRankB
      })
      .filter((mapper, index, self) => {
        // Remove duplicates based on "entity_field_label"
        return self.findIndex((m) => m.entity_field_label === mapper.entity_field_label) === index
      })
  })
  const filteredEntityMappers = computed<DataMapper[]>(() => {
    return entityMappers.value?.filter((mapper) => {
      if (props.showMappersWithValues) {
        return (
          !mapper.is_default &&
          mapper.label.toString().toLowerCase().includes(props.search.toLowerCase())
        )
      }
      return mapper.label.toLowerCase().includes(props.search.toLowerCase())
    })
  })

  const entityLabels = computed<string[]>(() => {
    return Array.from(
      new Set(
        props.mappers
          .filter((mapper) => {
            return mapper.entity_uuid === props.entityUuid
          })
          .map((mapper) => mapper.entity_label)
      )
    )
  })
  const isCompany = computed<boolean>(() => {
    return entityMappers.value.some(
      (mapper) => mapper.key.includes('is_company') && mapper.data === 'True'
    )
  })
  const firstName = computed<string>(() => {
    return entityMappers.value.find((mapper) => mapper.key.includes('first_name'))?.data as string
  })
  const lastName = computed<string>(() => {
    return entityMappers.value.find((mapper) => mapper.key.includes('last_name'))?.data as string
  })
  const companyName = computed<string>(() => {
    return entityMappers.value.find((mapper) => mapper.key.includes('company_name'))?.data as string
  })
  const legalForm = computed<string>(() => {
    return entityMappers.value.find((mapper) => mapper.key.includes('legal_form'))?.data as string
  })
  const name = computed<string>(() => {
    return filteredEntityMappers.value[0].entity_label
  })
  const isExpanded = ref(false)
  const hasValues = computed<boolean>(() => {
    return filteredEntityMappers.value.length > 0
  })
</script>
<template>
  <TableRow
    v-if="hasValues"
    class="cursor-pointer"
    @click="isExpanded = !isExpanded"
  >
    <TableCell>
      <span class="flex items-center gap-x-2">
        <span class="font-bold">{{ entityLabels.join(', ') }}</span>
        <span v-if="isExpanded">
          <ChevronDownIcon class="h-4 w-4" />
        </span>
        <span v-else>
          <ChevronRightIcon class="h-4 w-4" />
        </span>
      </span>
    </TableCell>
    <TableCell class="w-1/3">Name</TableCell>
    <TableCell class="w-1/3">
      <div class="flex items-center space-x-2">
        <template v-if="isCompany">
          <Building2Icon class="h-4 w-4" />
          <span>{{ companyName }} {{ legalForm }}</span>
        </template>
        <template v-else>
          <User class="h-4 w-4" />
          <span>{{ firstName }} {{ lastName }}</span>
        </template>
      </div>
    </TableCell>
    <TableCell class="w-1/3">
      <template v-if="!isExpanded">
        <span class="flex items-center space-x-2 text-gray-500">
          <ArrowDownNarrowWideIcon class="h-4 w-4" />
          <span>Mehrere Quellen</span>
        </span>
      </template>
    </TableCell>
  </TableRow>
  <template v-if="isExpanded">
    <TableRow
      v-for="mapper in filteredEntityMappers"
      :key="mapper.key"
    >
      <TableCell></TableCell>
      <TableCell class="w-1/3">{{ mapper.entity_field_label }}</TableCell>
      <TEMPORARYMapperDataTableCell
        class="w-1/3"
        :mapper="mapper"
      />
      <TEMPORARYMapperSourceTableCell
        class="w-1/3"
        :mapper="mapper"
      />
    </TableRow>
  </template>
</template>
